import { ReactElement, useEffect, useState } from 'react';
import moment from 'moment';
import 'moment-timezone';
import Header from '../Header';
import { Group, ParametersType, TimeSpan } from '../../constants/telosair-interfaces';
import { getDeviceSummaryOverTime } from '../../services/dashboard-service';
import Loader from '../loader';
import { useData } from '../../context/data.context';
import Graph from './graph';
import { getParameterByName } from '../../_helpers/globalHelper';
import { COLORS } from '../../constants/global-constants';

type Props = {
  timeSpanDate: { startDate: number; endDate: number };
  timeSpan: TimeSpan;
  data: { serialNumber: number; parameter: ParametersType } | null;
  selectedGroup: Group;
  /** Hide the internal "Time Series" title + legend when the parent already labels the chart. */
  hideHeader?: boolean;
  /** Accent colour for the line + area fill. */
  lineColor?: string;
  /** Fill the area under the line with a fading gradient. */
  fill?: boolean;
  /** Label shown before the value in the chart tooltip. */
  tooltipLabel?: string;
  /** Reports the plotted [timestamp, value] pairs to the parent (for CSV export). */
  onData?: (rows: number[][]) => void;
  /** Chart bucket width (dataPointsDifference); server auto-picks when omitted. */
  resolution?: string;
};

/**
 * Displays a histogram of device values and allows users to switch between devices.
 * @param props - DevicesChart.
 * @returns A React element representing the DevicesChart component.
 */
export default function DevicesChart(props: Props): ReactElement {
  const { userConfigData, loading: devicesLoading, parameters } = useData();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    timeSpanDate,
    timeSpan,
    data,
    selectedGroup,
    hideHeader,
    lineColor,
    fill,
    tooltipLabel,
    onData,
    resolution
  } = props;
  const [xAxis, setXAxis] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [lineChartGraphData, setLineChartGraphData] = useState<{
    [key: number]: { value: number[][]; bordercolor: string };
  }>({});

  /**
   * Get data from get-chart-data api based on the device client, facility, room and sensor.
   * @param deviceSerialNumber - Serial number of the device.
   * @returns - PArticled data for a device.
   */
  const getDeviceChartData = async (
    deviceSerialNumber: number
    // eslint-disable-next-line consistent-return
  ): Promise<any> => {
    try {
      const config = {
        startDate: timeSpanDate.startDate,
        endDate: timeSpanDate.endDate,
        devices: [deviceSerialNumber],
        ...(resolution ? { dataPointsDifference: resolution } : {})
      };
      const response = await getDeviceSummaryOverTime(config);
      if (response.data?.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };
  /**
   * Fetches getChartData api.
   */
  const fetchData = async (): Promise<void> => {
    // eslint-disable-next-line no-restricted-syntax
    let index = 0;
    const xAxisLabels = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const device of selectedGroup.sensorDetailsList) {
      // eslint-disable-next-line no-await-in-loop
      const detialedChartDataResponse = await getDeviceChartData(device.serial_number);
      if (detialedChartDataResponse?.xAxis && detialedChartDataResponse?.xAxis?.length > 0) {
        // eslint-disable-next-line no-unsafe-optional-chaining
        xAxisLabels.push(...detialedChartDataResponse?.xAxis);
      }
      const selecteParameterData = detialedChartDataResponse?.data?.filter(
        (item: { data: number[]; name: string }) => item.name === data?.parameter
      );
      if (selecteParameterData?.length > 0) {
        lineChartGraphData[device.serial_number] = {
          value: selecteParameterData[0].data,
          bordercolor: COLORS[index]
        };
        // Surface the first device's series so the parent can offer a CSV export.
        if (index === 0) onData?.(selecteParameterData[0].data);
      }
      // eslint-disable-next-line no-plusplus
      index++;
    }
    setLineChartGraphData({ ...lineChartGraphData });
    const timestampsSet: any = new Set(xAxisLabels);
    setXAxis(Array.from(timestampsSet));
    setLoading(false);
  };

  useEffect(() => {
    if (!devicesLoading && selectedGroup?.sensorDetailsList) fetchData();
  }, [timeSpanDate, selectedGroup, devicesLoading, data, resolution]);

  /**
   * Function to get x axis labels.
   * @param timestamp - Timestamp of the axis label.
   * @returns - XAxis label.
   */
  const getFormattedXAxisLabel = (timestamp: number): string => {
    const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeSpan === TimeSpan.Week1 || timeSpan === TimeSpan.Month1) {
      return moment(timestamp).tz(timeZone).format('HH:mm (Do MMM)');
    }
    return moment(timestamp).tz(timeZone).format('HH:mm');
  };
  const parameter = data?.parameter && getParameterByName(data?.parameter, parameters);
  return (
    <div className="w-full flex-1 flex flex-col relative h-full">
      {/* <div className="absolute border-l border-l-gray-500 top-16 bottom-12 left-[-0.625rem]" /> */}
      {!hideHeader && <Header sectionName="Time Series" showIcon />}
      <div className="flex flex-1 justify-between flex-col">
        {!hideHeader && (
          <div className="flex flex-wrap mt-2 gap-3">
            <div className={`flex gap-1 items-center text-white cursor-pointer `}>
              <span
                className="material-icons text-base"
                style={{
                  color: parameter?.color
                }}
              >
                circle
              </span>
              <div className="text-xs font-normal">{parameter?.shortName}</div>
            </div>
            {Object.entries(lineChartGraphData).map(([key, device]) => {
              const deviceName =
                selectedGroup.sensorDetailsList.find((d) => String(d.serial_number) === key)
                  ?.deviceName || key;
              return (
                <div key={key} className={`flex gap-1 items-center text-white cursor-pointer `}>
                  <span
                    className="material-icons text-base"
                    style={{
                      color: device.bordercolor
                    }}
                  >
                    square
                  </span>
                  <div className="text-xs font-normal">{deviceName}</div>
                </div>
              );
            })}
          </div>
        )}
        {loading ? (
          <Loader />
        ) : (
          <div className="flex flex-1 mt-4">
            <Graph
              deviceData={lineChartGraphData}
              labels={xAxis?.map((timestamp) => getFormattedXAxisLabel(timestamp))}
              lineColor={lineColor}
              fill={fill}
              tooltipLabel={tooltipLabel}
              dashed
            />
          </div>
        )}
      </div>
    </div>
  );
}
